// Kustomize Components page (roadmap item 43).
//
// Mounted at `/repos/:id/kustomize` for any repository whose `format` is
// `kustomize`. Shows the catalog of (component, tag) pairs the repo holds
// and, for the component the operator clicks, the per-layer breakdown
// plus a kustomization.yaml preview — that's the part of a Kustomize
// component that matters for review before promoting it to a base.
//
// Slug derivation matches the OllamaModels page: `useRepoDetail` is
// UUID-keyed, the OCI surface is slug-keyed, so we look up the project
// slug from the cached project list.

import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Boxes,
  Copy,
  FileCode,
  FileText,
  Layers,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useProjects, useRepoDetail } from '../../api/client';
import {
  type KustomizeComponentEntry,
  type KustomizeLayer,
  useKustomizationPreview,
  useKustomizeComponentDetail,
  useKustomizeComponents,
} from '../../api/kustomize';

function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function shortHex(d?: string): string {
  if (!d) return '';
  if (d.length <= 19) return d;
  return d.slice(0, 12) + '…' + d.slice(-4);
}

// Kustomize-specific layer media types, mapped to the translation key for the
// human label and the icon the catalog UI renders.
const LAYER_KIND_BY_MEDIA: Record<string, { labelKey: string; icon: typeof Boxes }> = {
  'application/vnd.cncf.kustomize.component.v1.tar+gzip': {
    labelKey: 'kustomizeComponents.layerKinds.componentBundle',
    icon: Boxes,
  },
  'application/vnd.cncf.kustomize.component.config.v1+json': {
    labelKey: 'kustomizeComponents.layerKinds.config',
    icon: FileText,
  },
};

function layerKindLabel(
  mediaType: string,
  t: (key: string) => string,
): { label: string; icon: typeof Boxes } {
  const entry = LAYER_KIND_BY_MEDIA[mediaType] ?? {
    labelKey: 'kustomizeComponents.layerKinds.layer',
    icon: Layers,
  };
  return { label: t(entry.labelKey), icon: entry.icon };
}

function componentsCountLabel(
  comps: { components: unknown[] } | undefined,
  t: (key: string, opts: { count: number }) => string,
): string | undefined {
  if (!comps) return undefined;
  return t('kustomizeComponents.catalog.count', { count: comps.components.length });
}

export function KustomizeComponents() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: repo, isLoading: repoLoading, error: repoError } = useRepoDetail(id);
  const { data: projects } = useProjects();
  const projectSlug = useMemo(
    () => projects?.find((p) => p.id === repo?.projectId)?.slug,
    [projects, repo?.projectId],
  );
  const { data: comps, error: compsError, isLoading: compsLoading } = useKustomizeComponents(
    projectSlug,
    repo?.slug,
  );
  const [selected, setSelected] = useState<KustomizeComponentEntry | null>(null);
  const detail = useKustomizeComponentDetail(
    projectSlug,
    repo?.slug,
    selected?.component,
    selected?.tag,
  );

  if (repoLoading) {
    return <div className="p-6 text-fg-muted">{t('kustomizeComponents.loadingRepo')}</div>;
  }
  if (repoError || !repo) {
    return (
      <div className="p-6 text-danger">
        {t('kustomizeComponents.loadRepoFailed', { error: String(repoError ?? 'unknown') })}
      </div>
    );
  }
  if (repo.format !== 'kustomize') {
    return (
      <div className="p-6">
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('kustomizeComponents.backToRepo')}
        </Link>
        <p className="text-fg-muted">
          <Trans
            i18nKey="kustomizeComponents.wrongFormat"
            components={[<code key={0} className="orb-code" />]}
          />
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('kustomizeComponents.backTo', { name: repo.name })}
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">{t('kustomizeComponents.eyebrow')}</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          {repo.name}
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          {t('kustomizeComponents.intro')}
        </p>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Boxes className="h-4 w-4 text-accent" />
              {t('kustomizeComponents.catalog.title')}
            </span>
          }
          description={componentsCountLabel(comps, t)}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">
                  {t('kustomizeComponents.catalog.table.component')}
                </th>
                <th className="px-4 py-2 font-medium">
                  {t('kustomizeComponents.catalog.table.tag')}
                </th>
                <th className="px-4 py-2 font-medium">
                  {t('kustomizeComponents.catalog.table.layers')}
                </th>
                <th className="px-4 py-2 font-medium">
                  {t('kustomizeComponents.catalog.table.totalSize')}
                </th>
                <th className="px-4 py-2 font-medium">
                  {t('kustomizeComponents.catalog.table.manifestDigest')}
                </th>
                <th className="px-4 py-2 font-medium">
                  {t('kustomizeComponents.catalog.table.uploaded')}
                </th>
              </tr>
            </thead>
            <tbody>
              {compsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    {t('kustomizeComponents.catalog.loading')}
                  </td>
                </tr>
              )}
              {compsError && (
                <tr>
                  <td className="px-4 py-3 text-danger" colSpan={6}>
                    {String(compsError)}
                  </td>
                </tr>
              )}
              {comps?.components.length === 0 && !compsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    <Trans
                      i18nKey="kustomizeComponents.catalog.empty"
                      values={{
                        cmd: `kustomize push oci://<host>/kustomize/${projectSlug}/${repo.slug}/<component>:<tag>`,
                      }}
                      components={[<code key={0} className="orb-code" />]}
                    />
                  </td>
                </tr>
              )}
              {comps?.components.map((c) => {
                const isSelected =
                  selected?.component === c.component && selected?.tag === c.tag;
                return (
                  <tr
                    key={`${c.component}:${c.tag}`}
                    className={`border-b border-border/50 cursor-pointer ${
                      isSelected ? 'bg-bg-hover/60' : 'hover:bg-bg-hover/40'
                    }`}
                    onClick={() => setSelected(c)}
                  >
                    <td className="px-4 py-2 font-medium text-fg">{c.component}</td>
                    <td className="px-4 py-2">
                      <Badge tone="accent">{c.tag}</Badge>
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {c.layer_count}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {bytes(c.total_size_bytes)}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-fg-muted">
                      {shortHex(c.digest)}
                    </td>
                    <td className="px-4 py-2 text-fg-muted">
                      {new Date(c.uploaded_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {comps && (
        <Card>
          <CardHeader title={t('kustomizeComponents.pull.title')} />
          <CardBody>
            <PullSnippet hint={comps.pull_hint} />
          </CardBody>
        </Card>
      )}

      {selected && (
        <>
          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <Layers className="h-4 w-4 text-accent" />
                  {selected.component}
                  <Badge tone="accent">{selected.tag}</Badge>
                </span>
              }
              description={t('kustomizeComponents.detail.layersDescription')}
            />
            <CardBody>
              {detail.isLoading && (
                <p className="text-fg-muted">{t('kustomizeComponents.detail.loading')}</p>
              )}
              {detail.error && (
                <p className="text-danger">{String(detail.error)}</p>
              )}
              {detail.data && <ComponentDetail detail={detail.data} />}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <FileCode className="h-4 w-4 text-accent" />
                  {t('kustomizeComponents.detail.title')}
                </span>
              }
              description={t('kustomizeComponents.detail.description')}
            />
            <CardBody>
              <KustomizationPreview
                projectSlug={projectSlug}
                repoSlug={repo.slug}
                component={selected.component}
                tag={selected.tag}
              />
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}

interface PullSnippetProps {
  readonly hint: string;
}

function PullSnippet({ hint }: PullSnippetProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(hint);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail in non-secure contexts; the operator can
      // always select the snippet manually so we don't surface an error.
    }
  };
  return (
    <div className="flex items-start gap-3">
      <pre className="flex-1 bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre">
        {hint}
      </pre>
      <Button
        variant="ghost"
        type="button"
        onClick={onCopy}
        title={t('kustomizeComponents.pull.copySnippet')}
      >
        <Copy className="h-4 w-4" />
        {copied ? t('tokens.created.copied') : t('tokens.created.copy')}
      </Button>
    </div>
  );
}

interface ComponentDetailProps {
  readonly detail: {
    component: string;
    tag: string;
    digest: string;
    size_bytes: number;
    uploaded_at: string;
    metadata: Record<string, unknown>;
    pull_hint: string;
  };
}

function ComponentDetail({ detail }: ComponentDetailProps) {
  const { t } = useTranslation();
  const meta = detail.metadata ?? {};
  const layers = (meta.layers as KustomizeLayer[] | undefined) ?? [];
  const total = (meta.total_size as number | undefined) ?? 0;
  const configDigest = meta.config_digest as string | undefined;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">
            {t('kustomizeComponents.detail.manifestDigest')}
          </dt>
          <dd className="font-mono text-xs text-fg break-all">{detail.digest}</dd>
        </div>
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">
            {t('kustomizeComponents.detail.totalSize')}
          </dt>
          <dd className="tabular-nums text-fg">{bytes(total || detail.size_bytes)}</dd>
        </div>
        {configDigest && (
          <div>
            <dt className="text-fg-muted text-xs uppercase tracking-wide">
              {t('kustomizeComponents.detail.configDigest')}
            </dt>
            <dd className="font-mono text-xs text-fg break-all">{configDigest}</dd>
          </div>
        )}
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">
            {t('kustomizeComponents.detail.uploaded')}
          </dt>
          <dd className="text-fg-muted">
            {new Date(detail.uploaded_at).toLocaleString()}
          </dd>
        </div>
      </dl>

      <div>
        <h3 className="text-sm font-semibold text-fg mb-2">
          {t('kustomizeComponents.detail.layersTitle', { count: layers.length })}
        </h3>
        {layers.length === 0 ? (
          <p className="text-fg-muted text-sm">
            {t('kustomizeComponents.detail.noLayers')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="px-3 py-2 font-medium">
                    {t('kustomizeComponents.detail.table.kind')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('kustomizeComponents.detail.table.mediaType')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('kustomizeComponents.detail.table.digest')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('kustomizeComponents.detail.table.size')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {layers.map((l) => {
                  const { label, icon: Icon } = layerKindLabel(l.media_type, t);
                  return (
                    <tr key={l.digest} className="border-b border-border/50">
                      <td className="px-3 py-2 text-fg">
                        <span className="inline-flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-accent" />
                          {label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {l.media_type}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {shortHex(l.digest)}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-fg-muted">
                        {bytes(l.size)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PullSnippet hint={detail.pull_hint} />
    </div>
  );
}

interface KustomizationPreviewProps {
  readonly projectSlug?: string;
  readonly repoSlug?: string;
  readonly component: string;
  readonly tag: string;
}

function KustomizationPreview({
  projectSlug,
  repoSlug,
  component,
  tag,
}: KustomizationPreviewProps) {
  const { t } = useTranslation();
  const { data, error, isLoading } = useKustomizationPreview(
    projectSlug,
    repoSlug,
    component,
    tag,
  );
  if (isLoading) {
    return <p className="text-fg-muted">{t('kustomizeComponents.preview.loading')}</p>;
  }
  if (error) {
    return (
      <p className="text-fg-muted text-sm">
        {t('kustomizeComponents.preview.unavailable', { error: String(error) })}
      </p>
    );
  }
  if (!data) return null;
  return (
    <pre className="bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre max-h-[480px]">
      {data}
    </pre>
  );
}
